import { useMemo, useState } from "react";
import { STATUSES } from "../../Component/EmployeeComponents/STATUSES";
import { monthLabels } from "../../Component/EmployeeComponents/monthLabels";
import formatCurrency from "../../Component/EmployeeComponents/formatCurrency";
import Pagination from "../../Component/Common/Pagination";
import LocationFilter from "../../Component/Common/LocationFilter";
import DateRangePicker from "../../Component/Common/DateRangePicker";
import RefreshButton from "../../Component/Common/RefreshButton";
import BlueButton from "../../Component/Common/BlueButton";
import RegularLoader from "../../Component/Loader/RegularLoader";
import { CheckCircle2, Eye, Pencil, RotateCcw, X, XCircle } from "lucide-react";
import EditLeadModal from "./EditLeadModal";
import { ViewLeadModal } from "./ViewLeadModal";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from "../../config/api";
import toast from "react-hot-toast";
import CreateLeadForm from "./CreateLeadForm";
import { formatDateTime } from "../../Component/Common/formatDateTime";

export default function LeadsTable({
  leads,
  pagination,
  setLeads,
  loading,
  leadTab,
  setLeadTab,
  filterStatus,
  setFilterStatus,
  onRefresh,
  fromDate,
  toDate,
  setFromDate,
  setToDate,
  locationType,
  setLocationType,
  locationValue,
  setLocationValue,
  canViewTeamMembers,
}) {

  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState("");

  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedLeadForView, setSelectedLeadForView] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [showCreateLead, setShowCreateLead] = useState(false);
  const [confirmData, setConfirmData] = useState(null);

  const [updating, setUpdating] = useState(false);

  const [remark, setRemark] = useState(""); 

  const confirmStatusChange = (leadId, status) => {
  setConfirmData({ leadId, status });
};

  /* ================= FILTERS ================= */
  const uniqueLocations = [...new Set(leads.map((l) => l.location))];

  const authUser = JSON.parse(localStorage.getItem("authUserData") || "{}");
  const userRole = Number(authUser?.role);
  
  const canEditLead = userRole === 1 || userRole === 2 || userRole === 4 || (userRole === 3 && authUser?.position === "MANAGER");
  const canSeeJunior =
    userRole === 1 || userRole === 2 || (userRole === 3 && authUser?.position === "MANAGER");

  const LEAD_TYPE_LABELS = {
    2: "Company",
    4: "Agent",
    5: "Retailer",
  };

//   const handleUpdateLeadStatus = async (leadId, status) => {
//   setUpdating(true);
//   const toastId = toast.loading("Updating status...");

//   try {
//     const res = await fetch(
//       `${BASE_URL}warranty/wlead/status/${leadId}`,
//       {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify({ status }),
//       }
//     );

//     const json = await res.json();

//     if (!json.status) {
//       throw new Error(json.message || "Update failed");
//     }

//     setLeads(prev =>
//       prev.map(l =>
//         l.id === leadId
//           ? { ...l, status }
//           : l
//       )
//     );

//     toast.success(`Lead marked as ${status}`, { id: toastId });

//   } catch (err) {
//     toast.error(err.message, { id: toastId });
//   } finally {
//     setUpdating(false);
//   }
// };


const handleUpdateLeadStatus = async (leadId, status, remarkText = "") => {
  setUpdating(true);
  const toastId = toast.loading("Updating status...");

  try {
    const authUser = JSON.parse(localStorage.getItem("authUserData") || "{}");

    const payload = {
      status,
      updated_by_id: authUser?.id,
      updated_by_name: authUser?.business_name ||`${authUser?.first_name || ""} ${authUser?.last_name || ""}`.trim(),
    };

    // ✅ Add remark only if lost
    if (status === "lost") {
      payload.remark = remarkText;
    }

    const res = await fetch(
      `${BASE_URL}warranty/wlead/status/${leadId}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }
    );

    const json = await res.json();

    if (!json.status) {
      throw new Error(json.message || "Update failed");
    }

    // ✅ Update UI instantly
    setLeads(prev =>
      prev.map(l =>
        l.id === leadId
          ? { ...l, status }
          : l
      )
    );

    toast.success(`Lead marked as ${status}`, { id: toastId });

  } catch (err) {
    toast.error(err.message, { id: toastId });
  } finally {
    setUpdating(false);
  }
};




  const filteredLeads = useMemo(() => {
    return leads
      .filter((l) => (filterStatus === "All" ? true : l.status === filterStatus))
      .filter((l) => {
        if (!startDate || !endDate) return true;
        const d = new Date(l.createdAt);
        return d >= startDate && d <= endDate;
      })
      .filter((l) =>
        selectedLocation ? l.location === selectedLocation : true
      );
  }, [leads, filterStatus, startDate, endDate, selectedLocation]);

  /* ================= PAGINATION ================= */
const paginatedLeads = filteredLeads;
const totalPages = pagination?.last_page || 1;

  const resetFilters = () => {
    setFilterStatus("All");
    setSelectedLocation("");
    setStartDate(null);
    setEndDate(null);
    setCurrentPage(1);
  };

  /* ================= UI ================= */
  return (
    <div className="bg-white rounded-2xl border border-blue-50 shadow-sm p-3 xl:min-h-[500px]">

      {/* ===== TABS ===== */}
      <div className="flex gap-2 mb-3">
        <button
          onClick={() => setLeadTab("my")}
          className={`px-4 py-2 rounded-lg text-sm font-semibold ${
            leadTab === "my"
              ? "bg-blue-600 text-white"
              : "bg-blue-50 text-blue-700"
          }`}
        >
          My Leads
        </button>

        {canSeeJunior && (   // ✅ FIX
          <button
            onClick={() => setLeadTab("junior")}
            className={`px-4 py-2 rounded-lg text-sm font-semibold ${
              leadTab === "junior"
                ? "bg-blue-600 text-white"
                : "bg-blue-50 text-blue-700"
            }`}
          >
            Team Leads
          </button>

          
          
        )}

        <div className="flex justify-end md:hidden">
          <button
            onClick={() => setShowCreateLead(true)}
            className="flex items-center justify-center rounded-lg bg-blue-600 text-white text-sm font-semibold px-4 py-2"
          >
            + Add Org
          </button>
        </div>
      </div>

      {/* ===== FILTERS ===== */}
     <div className="mb-4 space-y-3">

    {/* ================= MOBILE VIEW ================= */}
    <div className="md:hidden space-y-2">

      {/* ROW 1 */}
      <div className="flex gap-2 items-center">

  {/* Location Dropdown Wrapper */}
  <div className="flex-1 flex gap-2">

    {/* Location Type */}
    <select
      value={locationType}
      onChange={(e) => {
        setLocationType(e.target.value);
        setLocationValue("");
      }}
      className="flex-1 border rounded-lg px-2 py-2 text-xs"
    >
      <option value="">All Locations</option>
      <option value="state">State</option>
      <option value="district">District</option>
      <option value="pincode">Pincode</option>
    </select>

    {/* Conditional Select */}
    {locationType === "state" && (
      <select
        value={locationValue}
        onChange={(e) => setLocationValue(e.target.value)}
        className="flex-1 border rounded-lg px-2 py-2 text-xs"
      >
        <option value="">Select State</option>
        {[...new Set(leads.map(l => l.full?.state).filter(Boolean))].map(s => (
          <option key={s} value={s}>{s}</option>
        ))}
      </select>
    )}

    {locationType === "district" && (
      <select
        value={locationValue}
        onChange={(e) => setLocationValue(e.target.value)}
        className="flex-1 border rounded-lg px-2 py-2 text-xs"
      >
        <option value="">Select District</option>
        {[...new Set(leads.map(l => l.full?.district).filter(Boolean))].map(d => (
          <option key={d} value={d}>{d}</option>
        ))}
      </select>
    )}

    {locationType === "pincode" && (
      <input
        type="text"
        placeholder="Pincode"
        value={locationValue}
        onChange={(e) => setLocationValue(e.target.value)}
        className="flex-1 border rounded-lg px-2 py-2 text-xs"
      />
    )}
  </div>

  {/* Add Button */}
  

</div>

      {/* ROW 2 */}
      <div className="flex gap-2 items-center">

        <div className="flex-1">
          <DateRangePicker
            startDate={fromDate}
            endDate={toDate}
            onChange={(start, end) => {
              setFromDate(start);
              setToDate(end);
            }}
          />
        </div>

        <BlueButton
          onClick={resetFilters}
          className="flex items-center justify-center px-3 py-2"
        >
          {/* Mobile Icon */}
          <span className="md:hidden">
            <RotateCcw size={16} />
          </span>

          {/* Desktop Text */}
          <span className="hidden md:inline text-xs">
            Reset
          </span>
        </BlueButton>
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-2">

          {["All", ...STATUSES.map(s => s.value)].map((status) => {
            const isActive = filterStatus === status;

            const colorStyles =
              status === "won"
                ? "bg-green-100 text-green-700 border-green-200"
                : status === "lost"
                ? "bg-rose-100 text-rose-700 border-rose-200"
                : status === "in progress"
                ? "bg-amber-100 text-amber-700 border-amber-200"
                : status === "new"
                ? "bg-blue-100 text-blue-700 border-blue-200"
                : "bg-slate-100 text-slate-700 border-slate-200";

            return (
              <button
                key={status}
                onClick={() => {
                  setFilterStatus(status);
                  setCurrentPage(1);
                }}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-all
                  ${
                    isActive
                      ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                      : colorStyles
                  }
                `}
              >
                {status === "All"
                  ? "All"
                  : STATUSES.find(s => s.value === status)?.label || status}
              </button>
            );
          })}

        </div>

    </div>


    {/* ================= DESKTOP VIEW ================= */}
    <div className="hidden md:flex flex-wrap justify-end gap-2">

      <select
        value={filterStatus}
        onChange={(e) => setFilterStatus(e.target.value)}
        className="border rounded-lg px-2 py-1 text-sm"
      >
        <option value="All">All Statuses</option>
        {STATUSES.map((s) => (
          <option key={s.value} value={s.value}>
            {s.label}
          </option>
        ))}
      </select>

      <select
        value={locationType}
        onChange={(e) => {
          setLocationType(e.target.value);
          setLocationValue("");
        }}
        className="border rounded-lg px-2 py-1 text-sm"
      >
        <option value="">All Locations</option>
        <option value="state">State</option>
        <option value="district">District</option>
        <option value="pincode">Pincode</option>
      </select>

      {locationType === "state" && ( 
          <select value={locationValue} onChange={(e) => setLocationValue(e.target.value)} className="border rounded-lg px-2 py-1 text-sm" > 
          <option value="">Select State</option> 
          {[...new Set(leads.map(l => l.full?.state).filter(Boolean))].map(s => ( 
            <option key={s} value={s}>{s}</option> 
            ))} 
            </select> 
          )} 
        {locationType === "district" && ( 
          <select value={locationValue} onChange={(e) => setLocationValue(e.target.value)} className="border rounded-lg px-2 py-1 text-sm" > 
          <option value="">Select District</option> 
          {[...new Set(leads.map(l => l.full?.district).filter(Boolean))].map(d => ( 
            <option key={d} value={d}>{d}</option> 
            ))} 
            </select> 
          )} 
        {locationType === "pincode" && ( 
          <input 
            type="text" 
            placeholder="Enter pincode" 
            value={locationValue} 
            onChange={(e) => setLocationValue(e.target.value)} 
            className="border rounded-lg px-2 py-1 text-sm w-32" 
            /> 
          )}

      <DateRangePicker
        startDate={fromDate}
        endDate={toDate}
        onChange={(start, end) => {
          setFromDate(start);
          setToDate(end);
        }}
      />

      <BlueButton onClick={resetFilters}>Reset</BlueButton>
      <RefreshButton onClick={onRefresh} />
      <BlueButton onClick={() => setShowCreateLead(true)}>
        + Add Lead
      </BlueButton>
    </div>

  </div>


<div className="mt-2">

  {/* ================= MOBILE CARD VIEW ================= */}
  <div className="md:hidden space-y-4">
  {loading ? (
    <div className="py-6 text-center">
      <RegularLoader />
    </div>
  ) : paginatedLeads.length === 0 ? (
    <div className="py-10 items-center text-center text-slate-400 text-sm">
      No leads found
    </div>
  ) : (
    paginatedLeads.map((lead) => {
      const statusStyles =
        lead.status === "won"
          ? {
              badge: "bg-green-100 text-green-700",
              border: "border-l-green-500",
            }
          : lead.status === "lost"
          ? {
              badge: "bg-rose-100 text-rose-700",
              border: "border-l-rose-500",
            }
          : lead.status === "in progress"
          ? {
              badge: "bg-amber-100 text-amber-700",
              border: "border-l-amber-500",
            }
          : {
              badge: "bg-blue-100 text-blue-700",
              border: "border-l-blue-500",
            };

      return (
        <div
          key={lead.id}
          className={`bg-white rounded-2xl shadow-sm border border-slate-200 border-l-4 ${statusStyles.border} p-4 transition hover:shadow-md`}
        >
          {/* Header */}
          <div className="flex justify-between items-start">
            <div>
              <p className="text-[11px] text-slate-400 tracking-wide">
                {lead.lead_code}
              </p>

              <h3 className="text-base font-semibold text-slate-800 mt-1">
                {lead.name}
              </h3>

              <p className="text-xs text-slate-500 mt-1">
                {lead.owner} • {lead.location}
              </p>
            </div>

            <span
              className={`px-3 py-1 rounded-full text-[11px] font-medium ${statusStyles.badge}`}
            >
              {lead.status}
            </span>
          </div>

          {/* Divider */}
          <div className="my-3 h-px bg-slate-100" />

          {/* Info Grid */}
          <div className="grid grid-cols-2 gap-y-2 text-xs text-slate-600">
            <div>
              <span className="text-slate-400">Type</span>
              <div className="font-medium text-slate-800">
                {LEAD_TYPE_LABELS[lead.type]}
              </div>
            </div>

            <div>
              <span className="text-slate-400">Value</span>
              <div className="font-semibold text-slate-900">
                {formatCurrency(lead.value)}
              </div>
            </div>

            <div>
              <span className="text-slate-400">Date</span>
              <div>{formatDateTime(lead.createdAt)}</div>
            </div>

            <div>
              <span className="text-slate-400">Created By</span>
              <div className="truncate">{lead.created_by_name}</div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 mt-4">

            <button
              onClick={() => {
                setSelectedLeadForView(lead.full);
                setViewModalOpen(true);
              }}
              className="flex-1 py-2 rounded-lg text-xs font-medium bg-slate-100 text-slate-700 hover:bg-slate-200 transition"
            >
              View
            </button>

            {lead.status === "new" && canEditLead && (
              <>
                <button
                  onClick={() =>
                    navigate("/add-entity", {
                      state: {
                        lead: lead.full,
                        leadType: lead.type,
                      },
                    })
                  }
                  className="flex-1 py-2 rounded-lg text-xs font-medium bg-amber-100 text-amber-700 hover:bg-amber-200 transition"
                >
                  Edit
                </button>

                <button
                  onClick={() =>
                    confirmStatusChange(lead.id, "in progress")
                  }
                  className="flex-1 py-2 rounded-lg text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700 transition"
                >
                  ✔
                </button>

                <button
                  onClick={() =>
                    confirmStatusChange(lead.id, "lost")
                  }
                  className="flex-1 py-2 rounded-lg text-xs font-medium bg-rose-600 text-white hover:bg-rose-700 transition"
                >
                  ✕
                </button>
              </>
            )}
          </div>
        </div>
      );
    })
  )}
</div>


  {/* ================= DESKTOP TABLE ================= */}
  <div className="hidden md:block overflow-x-auto -mx-4 md:mx-0 p-2">
      <div className="overflow-x-auto -mx-4 md:mx-0 p-2">
        <table className="min-w-full text-left text-xs sm:text-sm p-2 text-center text-nowrap">
          <thead className="border-b border-slate-100 bg-slate-50">
            <tr>
              <th className="py-2 pl-2 sm:pl-4">Code</th>
              <th className="py-2 pl-2 sm:pl-4">Lead</th>
              <th className="px-1 sm:px-2">Owner</th>
              <th className="px-1 sm:px-2">Location</th>
              <th className="px-1 sm:px-2">Type</th>
              <th className="px-1 sm:px-2">Status</th>
              <th className="px-1 sm:px-2">Value</th>
              <th className="px-1 sm:px-2">Date</th>
              <th className="px-1 sm:px-2">Created By</th>
              <th className="px-1 sm:px-2">Action</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="9" className="py-6 text-center">
                  <RegularLoader />
                </td>
              </tr>
            ) : paginatedLeads.length === 0 ? (
              <tr>
                <td colSpan="10" className="py-16 text-center text-slate-400">
                  No leads found
                </td>
              </tr>
            ) : (
              paginatedLeads.map((lead) => {
                const statusColor =
                  lead.status === "won"
                    ? "bg-green-50 text-green-700 border-green-100"
                    : lead.status === "lost"
                    ? "bg-red-50 text-red-700 border-red-100"
                    : lead.status === "in progress"
                    ? "bg-amber-50 text-amber-700 border-amber-100"
                    : "bg-blue-50 text-blue-700 border-blue-100";

                    return(
                <tr key={lead.id} className="border-b border-slate-50 last:border-0">
                  <td className="py-2 pl-2 sm:pl-4">{lead.lead_code}</td>
                  <td className="py-2 pl-2 sm:pl-4 break-words">{lead.name}</td>
                  <td className="px-1 sm:px-2 break-words">{lead.owner}</td>
                  <td className="px-1 sm:px-2 break-words">{lead.location}</td>
                  <td className="px-1 sm:px-2">{LEAD_TYPE_LABELS[lead.type]}</td>
                  <td className="px-1 sm:px-2">
                    <span
                      className={`inline-flex px-3 py-0.5 rounded-full border text-xs ${statusColor} whitespace-nowrap`}
                    >
                      {lead.status}
                    </span>
                  </td>
                  <td className="px-1 sm:px-2">{formatCurrency(lead.value)}</td>
                  <td className="px-1 sm:px-2">{formatDateTime(lead.createdAt)}</td>
                  <td className="px-1 sm:px-2 text-balance">{lead.created_by_name}</td>
                  <td
                    className={`py-5 px-1 sm:px-2 flex items-center gap-1 sm:gap-2 
                      ${!(lead.status === "new" && canEditLead) ? "justify-center" : ""}
                    `}
                  >
                    <Eye
                      size={16}
                      className="cursor-pointer text-blue-600"
                      onClick={() => {
                        setSelectedLeadForView(lead.full);
                        setViewModalOpen(true);
                      }}
                    />

                    {lead.status === "new" && canEditLead && (
                      <>
                        <Pencil
                          size={16}
                          className="cursor-pointer text-amber-600"
                          onClick={() => {
                            navigate("/add-entity", {
                              state: {
                                lead: lead.full,
                                leadType: lead.type,
                              },
                            });
                          }}
                        />

                        <button
                          onClick={() => confirmStatusChange(lead.id, "in progress")}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700"
                        >
                          <CheckCircle2 size={14} />
                        </button>

                        <button
                          onClick={() => confirmStatusChange(lead.id, "lost")}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-rose-600 text-white hover:bg-rose-700"
                        >
                          <XCircle size={14} />
                        </button>
                      </>
                    )}
                  </td>

                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      </div>

      {totalPages > 1 && (
          <Pagination
            currentPage={pagination?.current_page || 1}
            totalPages={pagination?.last_page || 1}
            onPageChange={(page) => onRefresh(page)}
          />
        )}

        {viewModalOpen && (
          <ViewLeadModal
            lead={selectedLeadForView}
            onClose={() => setViewModalOpen(false)}
          />
        )}

        {editModalOpen && (
          <EditLeadModal
            lead={selectedLead}
            onClose={() => setEditModalOpen(false)}
          />
        )}

        {showCreateLead && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center">
            <div className="bg-white w-full max-w-5xl rounded-2xl shadow-xl relative">

              {/* Close button */}
              <button
                onClick={() => setShowCreateLead(false)}
                className="absolute top-3 right-3 text-slate-500 hover:text-slate-800"
              >
                <X size={20} />
              </button>

              {/* Form */}
              <div className="p-5 max-h-[90vh] w-3xl overflow-y-auto">
                <CreateLeadForm
                  onCreate={() => {
                    setShowCreateLead(false);
                    onRefresh(); // 🔄 refresh leads
                  }}
                />
              </div>

            </div>
          </div>
        )}

        {confirmData && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
              
              <h3 className="text-lg font-semibold text-slate-800 mb-2">
                Confirm Action
              </h3>

              <p className="text-sm text-slate-600 mb-4">
                Are you sure you want to mark this lead as{" "}
                <span className="font-semibold capitalize">
                  {confirmData.status}
                </span>
                ?
              </p>

              {/* ✅ Show textarea only for LOST */}
              {confirmData.status === "lost" && (
                <div className="mb-4">
                  <textarea
                    value={remark}
                    onChange={(e) => setRemark(e.target.value)}
                    placeholder="Enter reason for marking as lost..."
                    className="w-full border rounded-lg p-2 text-sm"
                    rows={3}
                  />

                  {!remark.trim() && (
                    <p className="text-xs text-red-500 mt-1">
                      Remark is required for lost status
                    </p>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setConfirmData(null);
                    setRemark("");
                  }}
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-slate-100 hover:bg-slate-200"
                >
                  Cancel
                </button>

                <button
                  disabled={
                    updating ||
                    (confirmData.status === "lost" && !remark.trim())
                  }
                  onClick={() => {
                    handleUpdateLeadStatus(
                      confirmData.leadId,
                      confirmData.status,
                      remark
                    );
                    setConfirmData(null);
                    setRemark("");
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium text-white ${
                    confirmData.status === "lost"
                      ? "bg-rose-600 hover:bg-rose-700"
                      : "bg-emerald-600 hover:bg-emerald-700"
                  } ${updating ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  {updating ? "Updating..." : "Yes, Confirm"}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}


// import { useState, useEffect, useMemo } from "react";
// import { STATUSES } from "../../Component/EmployeeComponents/STATUSES";
// import { monthLabels } from "../../Component/EmployeeComponents/monthLabels";
// import formatCurrency from "../../Component/EmployeeComponents/formatCurrency";
// import Pagination from "../../Component/Common/Pagination";
// import LocationFilter from "../../Component/Common/LocationFilter";
// import DateRangePicker from "../../Component/Common/DateRangePicker";
// import RefreshButton from "../../Component/Common/RefreshButton";
// import BlueButton from "../../Component/Common/BlueButton";
// import RegularLoader from "../../Component/Loader/RegularLoader";
// import { Eye, Pencil } from "lucide-react";
// import EditLeadModal from "./EditLeadModal";
// import { ViewLeadModal } from "./ViewLeadModal";
// import { BASE_URL } from "../../config/api";

// export default function LeadsTable() {
//   const [leads, setLeads] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState(null);

//   const [leadTab, setLeadTab] = useState("my"); // my | junior

//   const [currentPage, setCurrentPage] = useState(1);
//   const [startDate, setStartDate] = useState(null);
//   const [endDate, setEndDate] = useState(null);
//   const [selectedLocation, setSelectedLocation] = useState("");
//   const [filterStatus, setFilterStatus] = useState("All");

//   const [viewModalOpen, setViewModalOpen] = useState(false);
//   const [selectedLeadForView, setSelectedLeadForView] = useState(null);
//   const [selectedLead, setSelectedLead] = useState(null);
//   const [editModalOpen, setEditModalOpen] = useState(false);

//   const authUser = JSON.parse(localStorage.getItem("authUserData") || "{}");
//   const userRole = Number(authUser?.role);

//   const canEditLead = userRole === 1 || userRole === 2 || (userRole === 3 && authUser?.position === "manager");
//   const canSeeJunior =
//     userRole === 1 || userRole === 2 || (userRole === 3 && authUser?.position === "manager");

//   const LEAD_TYPE_LABELS = {
//     2: "Company",
//     4: "Agent",
//     5: "Retailer",
//   };

//   const pageSize = 7;

//   /* ================= FETCH LEADS ================= */
//   const fetchLeads = async () => {
//   setLoading(true);
//   setError(null);

//   try {
//     const companyId = authUser?.company_id;
//     const userId = authUser?.id;

//     const params = new URLSearchParams();

//     /* ===== ROLE-BASED PARAMS ===== */

//     // ROLE 1 → Admin (only my leads)
//     if (userRole === 1) {
//       params.append("created_by_id", userId);
//     }

//     // ROLE 2 → Company
//     if (userRole === 2) {
//       params.append("company_id", companyId);

//       if (leadTab === "my") {
//         params.append("created_by_id", userId);
//       }
//       // junior → only company_id
//     }

//     // ROLE 3 → Employee
//     if (userRole === 3) {
//       params.append("company_id", companyId);

//       if (authUser?.position === "manager") {
//         if (leadTab === "my") {
//           params.append("created_by_id", userId);
//         } else {
//           // junior leads
//           params.append("manager_id", authUser?.report_to);
//         }
//       }

//       if (authUser?.position === "executive") {
//         params.append("created_by_id", userId);
//       }
//     }

//     // ROLE 4 → Agent
//     if (userRole === 4) {
//       params.append("company_id", companyId);
//       params.append("created_by_id", userId);
//     }

//     const url = `${BASE_URL}warranty/wlead/list?${params.toString()}`;

//     const res = await fetch(url);
//     const data = await res.json();

//     if (!res.ok || !data.status) {
//       throw new Error(data.message || "Failed to fetch leads");
//     }

//     const mapped = data.data.map((lead) => ({
//       id: lead.id,
//       name: lead.name,
//       owner: lead.owner_name || "-",
//       location: lead.district || lead.state || "-",
//       type: lead.lead_type,
//       status: lead.status,
//       value: lead.lead_amount || 0,
//       createdAt: lead.created_at,
//       lead_code: lead.lead_code,
//       full: lead,
//     }));

//     setLeads(mapped);
//   } catch (err) {
//     setError(err.message);
//   } finally {
//     setLoading(false);
//   }
// };


//  useEffect(() => {
//   fetchLeads();
// }, [leadTab]);


//   /* ================= FILTERS ================= */
//   const uniqueLocations = [...new Set(leads.map((l) => l.location))];

//   const filteredLeads = useMemo(() => {
//     return leads
//       .filter((l) => (filterStatus === "All" ? true : l.status === filterStatus))
//       .filter((l) => {
//         if (!startDate || !endDate) return true;
//         const d = new Date(l.createdAt);
//         return d >= startDate && d <= endDate;
//       })
//       .filter((l) => (selectedLocation ? l.location === selectedLocation : true));
//   }, [leads, filterStatus, startDate, endDate, selectedLocation]);

//   /* ================= TAB SPLIT ================= */
//   const myLeads = useMemo(
//     () => filteredLeads.filter((l) => l.full?.created_by_id === authUser?.id),
//     [filteredLeads, authUser]
//   );

//   const juniorLeads = useMemo(
//     () => filteredLeads.filter((l) => l.full?.created_by_id !== authUser?.id),
//     [filteredLeads, authUser]
//   );

//   const visibleLeads = leadTab === "my" ? myLeads : juniorLeads;

//   /* ================= PAGINATION ================= */
//   const totalPages = Math.ceil(visibleLeads.length / pageSize);
//   const paginatedLeads = visibleLeads.slice(
//     (currentPage - 1) * pageSize,
//     currentPage * pageSize
//   );

//   const resetFilters = () => {
//     setFilterStatus("All");
//     setSelectedLocation("");
//     setStartDate(null);
//     setEndDate(null);
//     setCurrentPage(1);
//   };

//   /* ================= SAFETY ================= */
//   useEffect(() => {
//     if (!canSeeJunior && leadTab === "junior") {
//       setLeadTab("my");
//     }
//   }, [leadTab, canSeeJunior]);

//   /* ================= UI ================= */
//   return (
//     <div className="bg-white rounded-2xl border border-blue-50 shadow-sm p-4 flex flex-col h-full">
      
//       {/* ===== TABS ===== */}
//       <div className="flex gap-2 mb-3">
//         <button
//           onClick={() => {
//             setLeadTab("my");
//             setCurrentPage(1);
//           }}
//           className={`px-4 py-2 rounded-lg text-sm font-semibold transition
//             ${
//               leadTab === "my"
//                 ? "bg-blue-600 text-white"
//                 : "bg-blue-50 text-blue-700 hover:bg-blue-100"
//             }`}
//         >
//           My Leads
//         </button>

//         {canSeeJunior && (
//           <button
//             onClick={() => {
//               setLeadTab("junior");
//               setCurrentPage(1);
//             }}
//             className={`px-4 py-2 rounded-lg text-sm font-semibold transition
//               ${
//                 leadTab === "junior"
//                   ? "bg-blue-600 text-white"
//                   : "bg-blue-50 text-blue-700 hover:bg-blue-100"
//               }`}
//           >
//             Junior Leads
//           </button>
//         )}
//       </div>

//       {/* ===== FILTERS ===== */}
//       <div className="flex flex-wrap items-center justify-end mb-4 gap-2">
//         <select
//           value={filterStatus}
//           onChange={(e) => {
//             setFilterStatus(e.target.value);
//             setCurrentPage(1);
//           }}
//           className="text-xs sm:text-sm border border-slate-200 rounded-lg px-2 py-1"
//         >
//           <option value="All">All Statuses</option>
//           {STATUSES.map((s) => (
//             <option key={s.value} value={s.value}>
//               {s.label}
//             </option>
//           ))}
//         </select>

//         <LocationFilter
//           locations={uniqueLocations}
//           selected={selectedLocation}
//           onChange={(loc) => {
//             setSelectedLocation(loc);
//             setCurrentPage(1);
//           }}
//         />

//         <DateRangePicker
//           startDate={startDate}
//           endDate={endDate}
//           onChange={(s, e) => {
//             setStartDate(s);
//             setEndDate(e);
//             setCurrentPage(1);
//           }}
//         />

//         <BlueButton onClick={resetFilters}>Reset Filters</BlueButton>
//         <RefreshButton onClick={fetchLeads} />
//       </div>

//       {/* ===== TABLE ===== */}
//       <div className="overflow-x-auto -mx-4 md:mx-0 p-2">
//         <table className="min-w-full text-left text-xs sm:text-sm p-2">
//           <thead>
//             <tr className="border-b border-slate-100 bg-slate-50">
//               <th className="py-2 pl-2 sm:pl-4">Code</th>
//               <th className="py-2 pl-2 sm:pl-4">Lead</th>
//               <th className="px-1 sm:px-2">Owner</th>
//               <th className="px-1 sm:px-2">Location</th>
//               <th className="px-1 sm:px-2">Type</th>
//               <th className="px-1 sm:px-2">Status</th>
//               <th className="px-1 sm:px-2">Value</th>
//               <th className="px-1 sm:px-2">Date</th>
//               <th className="px-1 sm:px-2">Action</th>
//             </tr>
//           </thead>

//           <tbody>
//             {loading ? (
//               <tr>
//                 <td colSpan="9" className="py-6 text-center">
//                   <RegularLoader />
//                 </td>
//               </tr>
//             ) : paginatedLeads.length === 0 ? (
//               <tr>
//                 <td colSpan="9" className="py-6 text-center text-slate-400">
//                   No leads found.
//                 </td>
//               </tr>
//             ) : (
//               paginatedLeads.map((lead) => {
//                 const statusColor =
//                   lead.status === "won"
//                     ? "bg-green-50 text-green-700 border-green-100"
//                     : lead.status === "lost"
//                     ? "bg-red-50 text-red-700 border-red-100"
//                     : lead.status === "in progress"
//                     ? "bg-amber-50 text-amber-700 border-amber-100"
//                     : "bg-blue-50 text-blue-700 border-blue-100";

//                 return (
//                   <tr key={lead.id} className="border-b border-slate-50 last:border-0">
//                     <td className="py-2 pl-2 sm:pl-4">{lead.lead_code}</td>
//                     <td className="py-2 pl-2 sm:pl-4 break-words">{lead.name}</td>
//                     <td className="px-1 sm:px-2 break-words">{lead.owner}</td>
//                     <td className="px-1 sm:px-2 break-words">{lead.location}</td>
//                     <td className="px-1 sm:px-2">{LEAD_TYPE_LABELS[lead.type]}</td>

//                     <td className="px-1 sm:px-2">
//                       <span
//                         className={`inline-flex px-3 py-0.5 rounded-full border text-xs ${statusColor} whitespace-nowrap`}
//                       >
//                         {lead.status}
//                       </span>
//                     </td>

//                     <td className="px-1 sm:px-2">{formatCurrency(lead.value)}</td>
//                     <td className="px-1 sm:px-2">{monthLabels[new Date(lead.createdAt).getMonth()]}</td>

//                     <td className="px-1 sm:px-2 flex items-center gap-1 sm:gap-2">
//                       <Eye
//                         className="cursor-pointer text-blue-600"
//                         size={16}
//                         onClick={() => {
//                           setSelectedLeadForView(lead.full);
//                           setViewModalOpen(true);
//                         }}
//                       />

//                       {lead.status === "new" && canEditLead && (
//                         <Pencil
//                           className="cursor-pointer text-amber-600"
//                           size={16}
//                           onClick={() => {
//                             setSelectedLead(lead.full);
//                             setEditModalOpen(true);
//                           }}
//                         />
//                       )}
//                     </td>
//                   </tr>
//                 );
//               })

//             )}
//           </tbody>
//         </table>
//       </div>

//       {totalPages > 1 && (
//         <Pagination
//           currentPage={currentPage}
//           totalPages={totalPages}
//           onPageChange={setCurrentPage}
//         />
//       )}

//       {viewModalOpen && (
//         <ViewLeadModal
//           lead={selectedLeadForView}
//           onClose={() => setViewModalOpen(false)}
//         />
//       )}

//       {editModalOpen && (
//         <EditLeadModal
//           lead={selectedLead}
//           onClose={() => setEditModalOpen(false)}
//         />
//       )}
//     </div>
//   );
// }



// import { useState, useEffect, useMemo } from "react";

// import { STATUSES } from "../../Component/EmployeeComponents/STATUSES";
// import { monthLabels } from "../../Component/EmployeeComponents/monthLabels";
// import formatCurrency from "../../Component/EmployeeComponents/formatCurrency";
// import Pagination from "../../Component/Common/Pagination";
// import LocationFilter from "../../Component/Common/LocationFilter";
// import DateRangePicker from "../../Component/Common/DateRangePicker";
// import RefreshButton from "../../Component/Common/RefreshButton";
// import BlueButton from "../../Component/Common/BlueButton";
// import RegularLoader from "../../Component/Loader/RegularLoader";
// import { Eye, Pencil, Trash2 } from "lucide-react";
// import EditLeadModal from "./EditLeadModal";
// import { ViewLeadModal } from "./ViewLeadModal";
// import { BASE_URL } from "../../config/api";

// export default function LeadsTable() {
//   const [leads, setLeads] = useState([]);
//   const [loading, setLoading] = useState(false);  
//   const [error, setError] = useState(null);

//   const [currentPage, setCurrentPage] = useState(1);
//   const [startDate, setStartDate] = useState(null);
//   const [endDate, setEndDate] = useState(null);
//   const [selectedLocation, setSelectedLocation] = useState("");
//   const [filterStatus, setFilterStatus] = useState("All");

//   const [viewModalOpen, setViewModalOpen] = useState(false);
//   const [selectedLeadForView, setSelectedLeadForView] = useState(null);

//   const [selectedLead, setSelectedLead] = useState(null);
//   const [editModalOpen, setEditModalOpen] = useState(false);

//   const authUser = JSON.parse(localStorage.getItem("authUserData") || "{}");
//   const userRole = Number(authUser?.role);
//   const canEditLead = userRole === 1 || userRole === 2 || userRole === 4 ;

//   const LEAD_TYPE_LABELS = {
//     2: "Company",
//     4: "Agent",
//     5: "Retailer",
//   };

//   const pageSize = 7;

//   // Fetch leads from API
//   const fetchLeads = async () => {
//     setLoading(true);
//     setError(null);
//     try {
//       const res = await fetch(
//         `${BASE_URL}warranty/wlead/list`
//       );
//       const data = await res.json();

//       if (!res.ok || !data.status) {
//         throw new Error(data.message || "Failed to fetch leads");
//       }

//       // Map API response to expected fields
//       const mappedLeads = data.data.map((lead) => ({
//         id: lead.id,
//         name: lead.name,
//         owner: lead.owner_name || "-",
//         location: lead.district || lead.state || "-",
//         type: lead.lead_type || "-",
//         status: lead.status,
//         value: lead.lead_amount || 0,
//         createdAt: lead.created_at,
//         lead_code:lead.lead_code,

//         full: lead, // 🔥 store full API response here
//       }));


//       setLeads(mappedLeads);
//     } catch (err) {
//       setError(err.message);
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchLeads();
//   }, []);

//   // Extract unique locations (e.g. district/state)
//   const uniqueLocations = [...new Set(leads.map((l) => l.location))];

//   // Filter logic
//   const filteredLeads = useMemo(() => {
//     return leads
//       .filter((lead) =>
//         filterStatus === "All" ? true : lead.status === filterStatus
//       )
//       .filter((lead) => {
//         if (!startDate || !endDate) return true;
//         const created = new Date(lead.createdAt);
//         return created >= startDate && created <= endDate;
//       })
//       .filter((lead) =>
//         selectedLocation ? lead.location === selectedLocation : true
//       );
//   }, [leads, filterStatus, startDate, endDate, selectedLocation]);

//   const totalPages = Math.ceil(filteredLeads.length / pageSize);

//   const paginatedLeads = filteredLeads.slice(
//     (currentPage - 1) * pageSize,
//     currentPage * pageSize
//   );

//   const resetPage = () => setCurrentPage(1);

//   const resetFilters = () => {
//   setFilterStatus("All");
//   setSelectedLocation("");
//   setStartDate(null);
//   setEndDate(null);
//   setCurrentPage(1);
// };


//   return (
//     <div className="bg-white rounded-2xl border border-blue-50 shadow-sm p-4 flex flex-col h-full">
//       {/* === Filters Row === */}
//       <div className="flex flex-wrap items-center justify-end mb-4 gap-2">
//         {/* Status Filter */}
//         <select
//           value={filterStatus}
//           onChange={(e) => {
//             setFilterStatus(e.target.value);
//             resetPage();
//           }}
//           className="text-xs sm:text-sm border border-slate-200 rounded-lg px-2 py-1"
//         >
//           <option value="All">All Statuses</option>
//           {STATUSES.map((s) => (
//             <option key={s.value} value={s.value}>
//               {s.label}
//             </option>
//           ))}
//         </select>

//         {/* Location Filter */}
//         <LocationFilter
//           locations={uniqueLocations}
//           selected={selectedLocation}
//           onChange={(loc) => {
//             setSelectedLocation(loc);
//             resetPage();
//           }}
//         />

//         {/* Date Range */}
//         <DateRangePicker
//           startDate={startDate}
//           endDate={endDate}
//           onChange={(start, end) => {
//             setStartDate(start);
//             setEndDate(end);
//             resetPage();
//           }}
//         />

//         <BlueButton
//           onClick={resetFilters}
//           className="text-xs sm:text-sm border border-slate-300 rounded-lg bg-slate-50 hover:bg-slate-100 transition"
//         >
//           Reset Filters
//         </BlueButton>

//         <RefreshButton onClick={fetchLeads} />
//       </div>

//       {/* === Table === */}
//       <div className="overflow-x-auto -mx-4 md:mx-0 p-2">
//         <table className="min-w-full text-left text-xs sm:text-sm p-2">
//           <thead>
//             <tr className="border-b border-slate-100 bg-slate-50">
//               <th className="py-2 pl-2 sm:pl-4">Code</th>
//               <th className="py-2 pl-2 sm:pl-4">Lead</th>
//               <th className="px-1 sm:px-2">Owner</th>
//               <th className="px-1 sm:px-2">Location</th>
//               <th className="px-1 sm:px-2">Type</th>
//               <th className="px-1 sm:px-2">Status</th>
//               <th className="px-1 sm:px-2">Value</th>
//               <th className="px-1 sm:px-2">Date</th>
//               <th className="px-1 sm:px-2">Action</th>
//             </tr>
//           </thead>

//           <tbody>
//             {loading ? (
//               <tr>
//                 <td colSpan="8" className="py-6 text-center">
//                   <RegularLoader />
//                 </td>
//               </tr>
//             ) : paginatedLeads.length === 0 ? (
//               <tr>
//                 <td colSpan="8" className="py-6 text-center text-slate-400">
//                   No leads match your filters.
//                 </td>
//               </tr>
//             ) : (
//               paginatedLeads.map((lead) => {
//                 const d = new Date(lead.createdAt);
//                 const monthLabel = monthLabels[d.getMonth()] || "—";

//                 const statusColor =
//                   lead.status === "won"
//                     ? "bg-green-50 text-green-700 border-green-100"
//                     : lead.status === "lost"
//                     ? "bg-red-50 text-red-700 border-red-100"
//                     : lead.status === "in progress"
//                     ? "bg-amber-50 text-amber-700 border-amber-100"
//                     : "bg-blue-50 text-blue-700 border-blue-100";

//                 return (
//                   <tr key={lead.id} className="border-b border-slate-50 last:border-0">
//                     <td className="py-2 pl-2 sm:pl-4 ">{lead.lead_code}</td>
//                     <td className="py-2 pl-2 sm:pl-4 break-words">{lead.name}</td>
//                     <td className="px-1 sm:px-2 break-words">{lead.owner}</td>
//                     <td className="px-1 sm:px-2 break-words">{lead.location}</td>
//                     <td className="px-1 sm:px-2">{LEAD_TYPE_LABELS[lead.type] || "-"}</td>
//                     <td className="px-1 sm:px-2">
//                       <span
//                         className={`inline-flex px-3 py-0.5 rounded-full border text-xs ${statusColor} whitespace-nowrap`}
//                       >
//                         {lead.status}
//                       </span>
//                     </td>

//                     <td className="px-1 sm:px-2">{formatCurrency(lead.value)}</td>
//                     <td className="px-1 sm:px-2">{monthLabel}</td>
//                     <td className="px-1 sm:px-2 flex items-center gap-1 sm:gap-2">
//                       <button
//                         className="text-blue-600 hover:text-blue-800"
//                         onClick={() => {
//                           setSelectedLeadForView(lead.full);
//                           setViewModalOpen(true);
//                         }}
//                       >
//                         <Eye size={16} />
//                       </button>

//                       {lead.status === "new" && canEditLead && (
//                         <button
//                           className="text-amber-600 hover:text-amber-800"
//                           onClick={() => {
//                             setSelectedLead(lead.full);
//                             setEditModalOpen(true);
//                           }}
//                         >
//                           <Pencil size={16} />
//                         </button>
//                       )}


//                     </td>
//                   </tr>
//                 );
//               })
//             )}
//           </tbody>
//         </table>
//       </div>

//       {/* === Pagination === */}
//       {totalPages > 1 && (
//         <Pagination
//           currentPage={currentPage}
//           totalPages={totalPages}
//           onPageChange={setCurrentPage}
//         />
//       )}

//       {viewModalOpen && selectedLeadForView && (
//         <ViewLeadModal lead={selectedLeadForView} onClose={() => setViewModalOpen(false)} />
//       )}

//       {editModalOpen && (
//         <EditLeadModal lead={selectedLead} onClose={() => setEditModalOpen(false)} />
//       )}
//     </div>

//   );
// }




// import { useState, useMemo } from "react";

// import formatCurrency from "../../Component/EmployeeComponents/formatCurrency";
// import { monthLabels } from "../../Component/EmployeeComponents/monthLabels";
// import { STATUSES } from "../../Component/EmployeeComponents/STATUSES";
// import Pagination from "../../Component/Common/Pagination";
// import LocationFilter from "../../Component/Common/LocationFilter";
// import DateRangePicker from "../../Component/Common/DateRangePicker";
// import RefreshButton from "../../Component/Common/RefreshButton";


// export default function LeadsTable({ leads, filterStatus, setFilterStatus }) {
//   const [currentPage, setCurrentPage] = useState(1);
//   const [startDate, setStartDate] = useState(null);
//   const [endDate, setEndDate] = useState(null);
//   const [selectedLocation, setSelectedLocation] = useState("");

//   const pageSize = 7;

//   // Extract unique locations (e.g. city)
//   const uniqueLocations = [...new Set(leads.map((l) => l.location))];

//   // ✅ Filter logic
//   const filteredLeads = useMemo(() => {
//   return leads
//     .filter((lead) =>
//       filterStatus === "All" ? true : lead.status === filterStatus
//     )
//     .filter((lead) => {
//       if (!startDate || !endDate) return true;
//       const created = new Date(lead.createdAt);
//       return created >= startDate && created <= endDate;
//     })
//     .filter((lead) =>
//       selectedLocation ? lead.location === selectedLocation : true
//     );
// }, [leads, filterStatus, startDate, endDate, selectedLocation]);


//   const totalPages = Math.ceil(filteredLeads.length / pageSize);

//   const paginatedLeads = filteredLeads.slice(
//     (currentPage - 1) * pageSize,
//     currentPage * pageSize
//   );

//   // Reset page when filters change
//   const resetPage = () => setCurrentPage(1);

//   return (
//     <div className="bg-white rounded-2xl border border-blue-50 shadow-sm p-4 flex flex-col h-full">
      
//       {/* === Filters Row === */}
//       <div className="flex items-center justify-end mb-4 gap-3 flex-wrap">

//         {/* Status Filter */}
//         {/* Status Filter */}
//         <select
//           value={filterStatus}
//           onChange={(e) => {
//             setFilterStatus(e.target.value);
//             resetPage();
//           }}
//           className="text-xs md:text-sm border border-slate-200 rounded-lg px-2 py-1"
//         >
//           <option value="All">All Statuses</option>
//           {STATUSES.map((s) => (
//             <option key={s.value} value={s.value}>
//               {s.label}
//             </option>
//           ))}
//         </select>


//         {/* Location Filter */}
//         <LocationFilter
//           locations={uniqueLocations}
//           selected={selectedLocation}
//           onChange={(loc) => {
//             setSelectedLocation(loc);
//             resetPage();
//           }}
//         />

//         {/* Date Range */}
//         <DateRangePicker
//           startDate={startDate}
//           endDate={endDate}
//           onChange={(start, end) => {
//             setStartDate(start);
//             setEndDate(end);
//             resetPage();
//           }}
//         />
//         <RefreshButton />
//       </div>

//       {/* === Table === */}
//       <div className="overflow-x-auto -mx-4 md:mx-0">
//         <table className="min-w-full text-left text-xs md:text-sm">
//           <thead>
//             <tr className="border-b border-slate-100 bg-slate-50">
//               <th className="py-2 pl-4">Lead</th>
//               <th className="px-2">Owner</th>
//               <th className="px-2">Location</th>
//               <th className="px-2">Status</th>
//               <th className="px-2">Value</th>
//               <th className="px-2">Date</th>
//             </tr>
//           </thead>

//           <tbody>
//             {paginatedLeads.length === 0 && (
//               <tr>
//                 <td colSpan="5" className="py-6 text-center text-slate-400">
//                   No leads match your filters.
//                 </td>
//               </tr>
//             )}

//             {paginatedLeads.map((lead) => {
//               const d = new Date(lead.createdAt);
//               const monthLabel = monthLabels[d.getMonth()] || "—";

//               const statusColor =
//                 lead.status === "Won"
//                   ? "bg-green-50 text-green-700 border-green-100"
//                   : lead.status === "Lost"
//                   ? "bg-red-50 text-red-700 border-red-100"
//                   : lead.status === "In Progress"
//                   ? "bg-amber-50 text-amber-700 border-amber-100"
//                   : "bg-blue-50 text-blue-700 border-blue-100";

//               return (
//                 <tr key={lead.id} className="border-b border-slate-50 last:border-0">
//                   <td className="py-2 pl-4">{lead.name}</td>
//                   <td className="px-2">{lead.owner}</td>
//                   <td className="px-2">{lead.location}</td>
//                   <td className="px-2">
//                     <span className={`inline-flex px-2 py-0.5 rounded-full border text-xs ${statusColor}`}>
//                       {lead.status}
//                     </span>
//                   </td>
//                   <td className="px-2">{formatCurrency(lead.value)}</td>
//                   <td className="px-2">{monthLabel}</td>
//                 </tr>
//               );
//             })}
//           </tbody>
//         </table>
//       </div>

//       {/* === Pagination === */}
//       {totalPages > 1 && (
//         <Pagination
//           currentPage={currentPage}
//           totalPages={totalPages}
//           onPageChange={setCurrentPage}
//         />
//       )}
//     </div>
//   );
// }
